import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { DEFAULT_SEO_TITLE } from '@/lib/seo';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trash2, Search, ShieldAlert, Mail, Clock, Copy, Check, Hash } from 'lucide-react';
import { User } from '@/api/entities';

const IDENTIFIER_LABELS = {
  full_name: 'Full name',
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email',
  phone: 'Phone',
  dob: 'Date of birth',
  passport_number: 'Passport',
  frequent_flyer_number: 'Frequent flyer',
};

const formatTypeLabel = (type) => {
  if (!type) return '—';
  if (IDENTIFIER_LABELS[type]) return IDENTIFIER_LABELS[type];
  return String(type).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
};

const shortId = (id) => {
  const s = String(id || '');
  if (!s) return '—';
  return s.length > 12 ? `${s.slice(0, 8)}…${s.slice(-4)}` : s;
};

export default function BlackList() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState('identifiers'); // identifiers | incidents
  const [profiles, setProfiles] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isIncidentsRefreshing, setIsIncidentsRefreshing] = useState(false);
  const [expandedProfileId, setExpandedProfileId] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  const canAccess = !!currentUser && (currentUser.role === 'admin' || currentUser.app_metadata?.roles?.includes('admin'));

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(String(text || ''));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(prev => (prev === key ? null : prev)), 1200);
    } catch {
      // ignore clipboard errors
    }
  };

  const load = async (search = '') => {
    try {
      setIsRefreshing(true);
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');
      
      const resp = await fetch('/api/admin/blacklist/profiles/list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        credentials: 'omit',
        body: JSON.stringify({ searchTerm: search, limit: 300 })
      });
      
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || `HTTP ${resp.status}`);
      }
      
      const data = await resp.json();
      setProfiles(Array.isArray(data.data) ? data.data : []);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  const loadIncidents = async () => {
    try {
      setIsIncidentsRefreshing(true);
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');
      
      const resp = await fetch('/api/admin/blacklist/incidents/list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        credentials: 'omit',
        body: JSON.stringify({ limit: 300 })
      });
      
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || `HTTP ${resp.status}`);
      }
      
      const data = await resp.json();
      setIncidents(Array.isArray(data.data) ? data.data : []);
    } finally {
      setIsIncidentsRefreshing(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const me = await User.me();
        setCurrentUser(me);
        if (me && (me.role === 'admin' || me.app_metadata?.roles?.includes('admin'))) {
          await load('');
        }
      } catch (e) {
        // ignore
      } finally {
        setIsLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredProfiles = useMemo(() => {
    const s = String(searchTerm || '').trim().toLowerCase();
    if (!s) return profiles;
    return profiles.filter(p => {
      const ids = p.identifiers || [];
      const blob = [
        p.full_name,
        p.email,
        p.phone,
        p.dob,
        p.reason,
        ...ids.flatMap(i => [i.raw_value, i.normalized_value, i.identifier_type, i.reason])
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(s);
    });
  }, [profiles, searchTerm]);
  const filteredIncidents = useMemo(() => {
    const s = String(searchTerm || '').trim().toLowerCase();
    if (!s) return incidents;
    return incidents.filter(i => {
      const blob = JSON.stringify({
        user_email: i.user_email,
        abandoned_trip_id: i.abandoned_trip_id,
        matched: i.matched
      }).toLowerCase();
      return blob.includes(s);
    });
  }, [incidents, searchTerm]);

  const handleDelete = async (id) => {
    if (!id) return;
    if (!confirm('Remove this blacklist identifier?')) return;
    
    const { supabase } = await import('@/lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    
    const resp = await fetch(`/api/admin/blacklist/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      },
      credentials: 'omit'
    });
    
    if (!resp.ok) {
      const t = await resp.text();
      alert(t || 'Failed to delete');
      return;
    }
    
    setProfiles(prev => prev
      .map(p => ({
        ...p,
        identifiers: (p.identifiers || []).filter(i => i.id !== id)
      }))
      .filter(p => (p.identifiers || []).length > 0)
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <p className="text-white/60 font-normal">Loading…</p>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 font-normal">Administrator access required.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{DEFAULT_SEO_TITLE}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-6">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h2 className="text-2xl font-light text-white flex items-center gap-3">
            <ShieldAlert className="w-6 h-6 text-red-400" />
            Black List
          </h2>
      <p className="text-white/60 text-sm font-normal">Profiles with all identifiers nested.</p>
        </motion.div>

        <div className="flex gap-2 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (view === 'identifiers') load(searchTerm);
                }
              }}
              placeholder="Search by value/type/reason..."
              className="glass text-white border-white/20 pl-10 placeholder:text-white/40"
            />
          </div>
          <Button
            onClick={() => (view === 'identifiers' ? load(searchTerm) : loadIncidents())}
            disabled={view === 'identifiers' ? isRefreshing : isIncidentsRefreshing}
            className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
          >
            {(view === 'identifiers' ? isRefreshing : isIncidentsRefreshing) ? 'Refreshing…' : (view === 'identifiers' ? 'Search' : 'Refresh')}
          </Button>
          <Button
            onClick={async () => {
              const next = view === 'identifiers' ? 'incidents' : 'identifiers';
              setView(next);
              if (next === 'incidents' && incidents.length === 0) {
                await loadIncidents();
              }
            }}
            className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
          >
            {view === 'identifiers' ? 'View Incidents' : 'View Identifiers'}
          </Button>
        </div>

        {view === 'identifiers' ? (
          <Card className="glass-light border-white/10">
            <CardHeader>
            <CardTitle className="text-white font-normal">Profiles ({filteredProfiles.length})</CardTitle>
            </CardHeader>
            <CardContent>
            {filteredProfiles.length === 0 ? (
              <p className="text-white/60 font-normal">No blacklist profiles found.</p>
              ) : (
              <div className="space-y-4">
                {filteredProfiles.map((profile) => {
                  const identifiers = profile.identifiers || [];
                  const findId = (type) => identifiers.find(i => i.identifier_type === type);
                  const headerName = profile.full_name
                    || findId('full_name')?.raw_value
                    || findId('full_name')?.normalized_value
                    || [findId('first_name')?.raw_value, findId('last_name')?.raw_value].filter(Boolean).join(' ')
                    || [findId('first_name')?.normalized_value, findId('last_name')?.normalized_value].filter(Boolean).join(' ')
                    || profile.email
                    || findId('email')?.raw_value
                    || findId('email')?.normalized_value
                    || profile.phone
                    || findId('phone')?.raw_value
                    || findId('phone')?.normalized_value
                    || 'Blacklisted Profile';
                  const headerReason = profile.reason || identifiers.find(i => i.reason)?.reason || '—';

                  const isExpanded = expandedProfileId === profile.id;

                  return (
                    <div key={profile.id} className="rounded-2xl border border-white/10 overflow-hidden">
                      <div 
                        className="px-4 py-3 bg-white/5 cursor-pointer hover:bg-white/10 transition-colors flex items-center justify-between"
                        onClick={() => setExpandedProfileId(isExpanded ? null : profile.id)}
                      >
                        <div>
                          <div className="text-white text-sm font-medium">
                            {headerName}
                          </div>
                          <div className="text-white/60 text-xs font-normal">
                            {headerReason} · {identifiers.length} identifier{identifiers.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <div className="text-white/40">
                          {isExpanded ? '▲' : '▼'}
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="divide-y divide-white/10">
                          {identifiers.map((r) => (
                          <div key={r.id} className="py-3 px-4 flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <div className="text-white text-sm font-medium truncate">
                                  <span className="text-white/60">{formatTypeLabel(r.identifier_type)}</span>
                                  {' · '}
                                  <span className="font-mono">{r.raw_value || r.normalized_value}</span>
                              </div>
                              <div className="text-white/60 text-xs font-normal mt-1">
                                {r.reason ? `Reason: ${r.reason}` : '—'}
                              </div>
                              <div className="text-white/40 text-[11px] font-normal mt-1 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDate(r.created_at)}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              title="Delete identifier"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      )}
                  </div>
                  );
                })}
              </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="glass-light border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-normal">Blocked Attempts ({filteredIncidents.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredIncidents.length === 0 ? (
                <p className="text-white/60 font-normal">No incidents recorded yet.</p>
              ) : (
                <div className="space-y-3">
                  {filteredIncidents.map((incident) => {
                    const matched = Array.isArray(incident.matched) ? incident.matched : [];
                    const reasons = Array.from(new Set(matched.map(m => m?.reason).filter(Boolean)));
                    const sharedReason = reasons.length === 1 ? reasons[0] : null;
                    const tripId = incident.abandoned_trip_id || '';
                    const tripCopyKey = `trip-${incident.id}`;

                    return (
                      <div
                        key={incident.id}
                        className="rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors p-4"
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Mail className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
                              <span className="text-white text-sm font-medium truncate">
                                {incident.user_email || 'Guest'}
                              </span>
                              <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 text-[10px] font-normal uppercase tracking-wider border border-red-500/20">
                                Blocked
                              </span>
                              {sharedReason && (
                                <span className="px-2 py-0.5 rounded-full bg-white/5 text-white/70 text-[10px] font-normal border border-white/10">
                                  {sharedReason}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1.5 text-xs text-white/50 font-normal flex-wrap">
                              <span className="flex items-center gap-1.5">
                                <Clock className="w-3 h-3" />
                                {formatDate(incident.created_at)}
                              </span>
                              {tripId && (
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(tripId, tripCopyKey)}
                                  className="flex items-center gap-1.5 hover:text-white/80 transition-colors group"
                                  title={`Copy trip ID: ${tripId}`}
                                >
                                  <Hash className="w-3 h-3" />
                                  <span className="font-mono">{shortId(tripId)}</span>
                                  {copiedKey === tripCopyKey ? (
                                    <Check className="w-3 h-3 text-green-400" />
                                  ) : (
                                    <Copy className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {matched.length > 0 && (
                          <div className="mt-3 ml-5 pl-3 border-l border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                            {matched.map((m, idx) => (
                              <div
                                key={`${incident.id}-${idx}`}
                                className="flex items-baseline gap-2 text-xs min-w-0"
                              >
                                <span className="text-white/40 font-normal w-24 flex-shrink-0">
                                  {formatTypeLabel(m.identifier_type)}
                                </span>
                                <span className="font-mono text-white/90 truncate">
                                  {m.raw_value_in_request || m.normalized_value || '—'}
                                </span>
                                {!sharedReason && m.reason && (
                                  <span className="text-white/40 font-normal truncate">· {m.reason}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}

